import type { StepConfig, FormValues, AddMoreConfig } from '../types';
import { evaluateConditions } from './conditions';

/**
 * Get addMore config for a step
 */
export function getAddMoreConfig(step: StepConfig): AddMoreConfig | null {
  if (!step.addMore || !step.addMoreConfig) return null;
  return step.addMoreConfig;
}

/**
 * Check if addMore is enabled for a step based on enableCondition
 */
export function isAddMoreEnabled(step: StepConfig, values: FormValues): boolean {
  const config = getAddMoreConfig(step);
  if (!config) return false;

  if (!config.enableCondition) return true; // No condition = always enabled

  return evaluateConditions(config.enableCondition, values);
}

/**
 * Get auto-show condition function from config
 */
export function getAutoShowCondition(
  config: AddMoreConfig
): ((values: FormValues) => boolean) | undefined {
  if (!config.autoShowCondition) return undefined;

  const { type, fieldId, operator, value } = config.autoShowCondition;

  if (type === 'field' && fieldId && operator && value !== undefined) {
    return (vals: FormValues) => {
      const fieldValue = vals[fieldId];

      if (operator === 'eq') {
        return fieldValue === value;
      }

      if (operator === 'in' && Array.isArray(value)) {
        return value.includes(fieldValue);
      }

      return false;
    };
  }

  return undefined;
}

/**
 * Find fieldGroup index for addMore fields
 * Looks for fieldGroup containing any of the addMore fieldIds
 */
export function findAddMoreFieldGroupIndex(step: StepConfig, config: AddMoreConfig): number {
  // If explicitly configured, use it
  if (config.fieldGroupIndex !== undefined) {
    return config.fieldGroupIndex;
  }

  // Auto-detect: find fieldGroup containing any addMore field
  if (step.fieldGroups) {
    return step.fieldGroups.findIndex((group) =>
      group.fields?.some((field) => config.fieldIds.includes(field.id))
    );
  }

  return -1;
}
